<template>
  <div class="chest-reward flex flex-col items-center justify-center py-4">
    <!-- Reward Animation -->
    <Transition
      enter-active-class="transition-all duration-500 ease-out"
      enter-from-class="opacity-0 scale-80"
      enter-to-class="opacity-100 scale-100"
    >
      <div
        v-if="showReward"
        class="text-center"
      >
        <!-- Avatar Reward -->
        <div
          v-if="chest.reward_type === 'avatar' && rewardData.shop_item_id"
          class="flex flex-col items-center"
        >
          <div class="w-32 h-32 rounded-xl overflow-hidden bg-muted/50 mb-4 border-4 border-primary">
            <img
              v-if="shopItem?.image_url"
              :src="shopItem.image_url"
              :alt="shopItem.name"
              class="w-full h-full object-cover"
            />
            <div
              v-else
              class="w-full h-full flex items-center justify-center"
            >
              <LucideIcon
                name="user"
                :size="36"
                class="text-muted-foreground"
              />
            </div>
          </div>
          <h3 class="text-xl font-bold text-white mb-2">
            {{ shopItem?.name || $t('chest.reward.avatar.description', 'You unlocked a new avatar!') }}
          </h3>
          <!-- C'est ici que la rareté compte le plus : le coffre est le seul
               moment où l'enfant reçoit sans choisir (HARMONIA-48). -->
          <RarityBadge v-if="shopItem" :rarity="shopItem.rarity" class="mb-2" />
        </div>

        <!-- Coins Reward -->
        <div
          v-else-if="chest.reward_type === 'coins' && rewardData.amount"
          class="flex flex-col items-center"
        >
          <div class="relative mb-5">
            <!-- Halo doré derrière la pièce -->
            <div class="absolute inset-0 rounded-full bg-amber-400/25 blur-2xl scale-150 pointer-events-none" />
            <SpinningCoin :size="128" :thickness="14"  />
            <div v-if="rewardData.amount > 1" class="absolute top-0 right-0 bg-primary-500 text-ink text-lg font-bold px-2 py-1 rounded-full z-10 border-2">
              x{{ rewardData.amount }}
            </div>

          </div>
          <h3 class="text-xl font-bold text-white mb-1">
            {{ t('chest.reward.coins.description', { amount: rewardData.amount }) }}
          </h3>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import SpinningCoin from '@/components/coins/SpinningCoin.vue';
import { shopService } from '@/services/shop.service';
import type { Chest } from '@/models/Chest';
import type { ShopItem } from '@/models/ShopItem';
import RarityBadge from '@/components/shop/RarityBadge.vue';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    chest: Chest;
    /** When true, claim only emits claim-complete without calling the API (e.g. onboarding demo). */
    isOnboarding?: boolean;
  }>(),
  { isOnboarding: false }
);

const showReward = ref(false);
const shopItem = ref<ShopItem | null>(null);
const rewardData = computed(() => {
  try {
    return JSON.parse(props.chest.reward_value);
  } catch {
    return {};
  }
});

onMounted(() => {
  // Show reward after a short delay for animation
  setTimeout(() => {
    showReward.value = true;
    
    // Load shop item if it's an avatar reward
    if (props.chest.reward_type === 'avatar' && rewardData.value.shop_item_id) {
      loadShopItem(rewardData.value.shop_item_id);
    }
  }, 500);
});

const loadShopItem = async (itemId: string) => {
  try {
    const items = await shopService.getShopItems();
    const item = items.find(i => i.id === itemId);
    if (item) {
      shopItem.value = item;
      console.log('✅ [ChestReward] Loaded shop item for reward:', item.name);
    } else {
      console.warn(`⚠️ [ChestReward] Shop item not found for reward: ${itemId}`);
    }
  } catch (error: any) {
    console.error('❌ [ChestReward] Error loading shop item:', error);
    // Non-critical: reward will still be claimable, just without item details
  }
};
</script>

<!-- Styles moved to Tailwind classes in template - no scoped styles per project rules -->
